import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Scissors, Sparkles, Eye, Flower2, Clock } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { Reveal, SectionHeader, EASE } from "./Reveal";

const CAT_ICONS = {
    "Barbería": Scissors,
    "Estética": Sparkles,
    "Spa": Flower2,
    "Cosmetología": Sparkles,
};

export default function Services() {
    const { content, t, lang, cf } = useSite();
    const [cat, setCat] = useState("Todos");

    const services = content?.services || [];
    const categories = ["Todos", ...new Set(services.map((s) => s.category).filter(Boolean))];
    const filtered = cat === "Todos" ? services : services.filter((s) => s.category === cat);
    const catLabel = (c) => t.cats[c] || c;
    const svcName = (s) => (lang === "en" && s.name_en ? s.name_en : s.name);
    const svcDesc = (s) => (lang === "en" && s.description_en ? s.description_en : s.description);

    return (
        <section id="servicios" data-testid="services-section" className="relative py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                    <SectionHeader
                        eyebrow={t.services.eyebrow}
                        title={cf("about_title") || "Rituales para cada estilo"}
                        subtitle={cf("about_text")}
                    />
                    <Reveal delay={0.2} className="shrink-0">
                        <div className="flex flex-wrap gap-2" data-testid="services-filter">
                            {categories.map((c) => (
                                <button
                                    key={c}
                                    data-testid={`services-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                                    onClick={() => setCat(c)}
                                    className={`px-4 py-2 rounded-full text-xs tracking-widest uppercase transition-all duration-300 border ${
                                        cat === c
                                            ? "bg-gold-gradient text-black border-transparent font-semibold"
                                            : "border-gold/25 text-zinc-300 hover:border-gold/60 hover:text-gold-light"
                                    }`}
                                >
                                    {c === "Todos" ? t.services.all : catLabel(c)}
                                </button>
                            ))}
                        </div>
                    </Reveal>
                </div>

                <motion.div layout className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <AnimatePresence mode="popLayout">
                        {filtered.map((s, i) => {
                            const Icon = CAT_ICONS[s.category] || Sparkles;
                            return (
                                <motion.article
                                    layout
                                    key={s.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    viewport={{ once: true, amount: 0.1 }}
                                    transition={{ duration: 0.6, delay: (i % 4) * 0.07, ease: EASE }}
                                    data-testid={`service-card-${s.id}`}
                                    className="group relative rounded-2xl border border-gold/15 bg-surface p-7 hover:border-gold/45 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(212,175,55,0.08)] transition-all duration-500 flex flex-col h-full"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="h-11 w-11 rounded-full border border-gold/30 bg-gold/5 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-black transition-all duration-500">
                                            <Icon size={18} />
                                        </div>
                                        <span className="text-[10px] uppercase tracking-[0.25em] text-gold/60">{catLabel(s.category)}</span>
                                    </div>
                                    <h3 className="mt-6 font-serif text-xl text-amber-100 leading-snug" data-testid={`service-name-${s.id}`}>
                                        {svcName(s)}
                                    </h3>
                                    {svcDesc(s) && (
                                        <p className="mt-3 text-sm text-zinc-400 font-light leading-relaxed flex-1">{svcDesc(s)}</p>
                                    )}
                                    {s.duration && (
                                        <div className="mt-6 pt-5 border-t border-gold/10">
                                            <span className="flex items-center gap-1.5 text-xs text-zinc-500">
                                                <Clock size={12} className="text-gold/70" />
                                                {s.duration}
                                            </span>
                                        </div>
                                    )}
                                </motion.article>
                            );
                        })}
                    </AnimatePresence>
                </motion.div>

                {filtered.length === 0 && (
                    <p className="mt-14 text-center text-zinc-500 font-light" data-testid="services-empty">
                        {t.services.empty}
                    </p>
                )}
            </div>
        </section>
    );
}
